import React, {useState} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import ModalComponent from 'components/ModalComponent';
import { modals } from 'ducks/index';
import './styles/DefaultModals.scss';

export const MessageModal = connect()(({
  message,
  title = 'Success',
  onClose = null
}) => {
  let messageBody;
  if (React.isValidElement(message)) {
    messageBody = message;
  } else {
    messageBody = <p>{message}</p>;
  }
  return (
    <ModalComponent title={title} onClose={onClose}>
      {messageBody}
    </ModalComponent>
  );
});

export const UnexpectedError = connect()(({
  message = "We're sorry, something went wrong. Please reload the page and try again.",
  onClose = null
}) => <MessageModal title='Oops!' message={message} onClose={onClose} />);

const confirmAndClose = (dispatch, onConfirm) => {
  onConfirm();
  dispatch(modals.actions.hide());
};

export const VerificationModal = connect()(({
  confirmButtonType,
  confirmText,
  dispatch,
  message,
  onConfirm,
  title = 'Warning',
  onClose,
  ...modalProps
}) => (
  <ModalComponent
    title={title}
    buttons={[
      (
        <button
          type='button'
          className={`btn btn-${confirmButtonType}`}
          onClick={() => confirmAndClose(dispatch, onConfirm)}
        >
          {confirmText}
        </button>
      )
    ]}
    onClose={onClose}
    {...modalProps}
  >
    {message}
  </ModalComponent>
));

VerificationModal.defaultProps = {
  confirmButtonType: 'danger',
  confirmText: 'Confirm'
};

VerificationModal.propTypes = {
  confirmButtonType: PropTypes.string,
  confirmText: PropTypes.string
};

/**
 * A confirmation modal that requires the user to check a box before they can submit the modal.
 *
 * @param {string} confirmButtonType           Bootstrap button type to use for the confirm button
 * @param {function} dispatch                  Redux dispatch function
 * @param {React.ReactElement} message         The main modal body
 * @param {React.ReactElement} checkboxMessage Checkbox label message
 * @param {array} checkboxObjects              Checkbox objects
 * @param {function} onConfirm                 Callback invoked when confirmation is submitted
 * @param {string} title                       Title for the modal dialog
 * @param {function} onClose                   Callback invoked if modal is closed without submitting
 * @returns {*}
 * @constructor
 */
export const ExplicitVerificationModal = connect()(({
  confirmButtonType,
  dispatch,
  message,
  checkboxObjects,
  onConfirm,
  title = 'Warning',
  onClose
}) => {
  const [currentCheckboxObjects, setCurrentCheckboxObjects] = useState(checkboxObjects);

  const updateCheckboxObjects = (checkboxId) => {
    const newCheckboxObjects = currentCheckboxObjects.map((checkboxObject) => {
      if (checkboxObject.id === checkboxId) {
        checkboxObject.is_checked = !checkboxObject.is_checked;
      }

      return checkboxObject;
    });

    setCurrentCheckboxObjects(newCheckboxObjects);
  };

  const disableConfirm = () => {
    return currentCheckboxObjects.some((currentCheckboxObject) => currentCheckboxObject.is_checked === false);
  };

  return (
    <ModalComponent
      title={title}
      className='explicit-verification-modal'
      buttons={[
        (
          <button
            type='button'
            disabled={disableConfirm()}
            className={`btn btn-${confirmButtonType}`}
            onClick={() => confirmAndClose(dispatch, onConfirm)}
          >
          Confirm
          </button>
        )
      ]}
      onClose={onClose}
    >
      {message}

      {currentCheckboxObjects.map((checkboxObject) => (
        <div key={checkboxObject.id}>
          <label className='explicit-verification-checkbox' >
            <input type='checkbox' disabled={checkboxObject.is_disabled}
                   checked={checkboxObject.is_checked} onChange={() => updateCheckboxObjects(checkboxObject.id)} />&nbsp;
            {checkboxObject.message}
          </label>
        </div>
      ))}


    </ModalComponent>
  );
});

export const generateCheckboxObject = (id, message, is_checked = false, is_disabled = false) => ({
  id,
  message,
  is_checked,
  is_disabled
});

ExplicitVerificationModal.defaultProps = {
  confirmButtonType: 'danger'
};

ExplicitVerificationModal.propTypes = {
  confirmButtonType: PropTypes.string
};
